import React, { useEffect, useState } from 'react';
import './Admin.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'sl_admin_token';

const ADMIN_ACTIONS = [
  {
    id: 'create-w-file',
    label: 'Create W template',
    description: 'Tokenize test-s.xml into test-w.xml from the database.',
  },
  {
    id: 'export-s-answers',
    label: 'Export sandhi answers',
    description: 'Write test-s-answers.xml from saved sentence text.',
  },
  {
    id: 'export-w-answers',
    label: 'Export morphology answers',
    description: 'Write test-w-answers.xml from saved morphology rows.',
  },
];

async function adminRequest(path, token, method = 'POST') {
  const res = await fetch(`${API_BASE_URL}/api/admin/${path}`, {
    method,
    headers: { 'X-Admin-Token': token },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || 'Request failed');
  }
  return data;
}

function Admin() {
  const [tokenInput, setTokenInput] = useState('');
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(Boolean(token));
  const [authError, setAuthError] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [results, setResults] = useState({});

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await adminRequest('verify', token, 'GET');
        if (!cancelled) {
          setAuthenticated(true);
          setAuthError(null);
        }
      } catch (err) {
        if (!cancelled) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken('');
          setAuthenticated(false);
          setAuthError(err.message || 'Invalid token');
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const value = tokenInput.trim();
    if (!value) return;

    setChecking(true);
    setAuthError(null);
    try {
      await adminRequest('verify', value, 'GET');
      sessionStorage.setItem(TOKEN_KEY, value);
      setToken(value);
      setAuthenticated(true);
      setTokenInput('');
    } catch (err) {
      setAuthError(err.message || 'Invalid token');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthenticated(false);
    setResults({});
    setAuthError(null);
  };

  const runAction = async (actionId) => {
    setRunningId(actionId);
    setResults((prev) => ({ ...prev, [actionId]: null }));
    try {
      const data = await adminRequest(actionId, token);
      setResults((prev) => ({
        ...prev,
        [actionId]: { ok: true, message: data.message || 'Done.' },
      }));
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [actionId]: { ok: false, message: err.message || 'Failed.' },
      }));
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-card">
        <header className="admin-header">
          <h1>Admin</h1>
          <a href="/" className="admin-back-link">
            Back to app
          </a>
        </header>

        {checking && !authenticated ? (
          <p className="admin-status">Checking token…</p>
        ) : !authenticated ? (
          <form className="admin-login" onSubmit={handleLogin}>
            <label htmlFor="admin-token">Admin token</label>
            <input
              id="admin-token"
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Enter token"
              autoComplete="off"
              autoFocus
            />
            {authError && <p className="admin-error">{authError}</p>}
            <button type="submit" disabled={checking || !tokenInput.trim()}>
              Continue
            </button>
          </form>
        ) : (
          <div className="admin-actions">
            {ADMIN_ACTIONS.map((action) => {
              const result = results[action.id];
              return (
                <div key={action.id} className="admin-action">
                  <div className="admin-action-text">
                    <strong>{action.label}</strong>
                    <span>{action.description}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => runAction(action.id)}
                    disabled={runningId !== null}
                  >
                    {runningId === action.id ? 'Running…' : 'Run'}
                  </button>
                  {result && (
                    <p
                      className={
                        result.ok ? 'admin-result admin-result-ok' : 'admin-result admin-result-err'
                      }
                    >
                      {result.message}
                    </p>
                  )}
                </div>
              );
            })}
            <button type="button" className="admin-logout" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
